import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/lib/auth-context";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppShell from "@/components/AppShell";
import Login from "@/pages/Login";
import Users from "@/pages/Users";
import Organisation from "@/pages/Organisation";
import Branding from "@/pages/Branding";
import AuditLog from "@/pages/AuditLog";
import Placeholder from "@/pages/Placeholder";
import Upload from "@/pages/Upload";
import Documents from "@/pages/Documents";
import DocumentDetail from "@/pages/DocumentDetail";
import Reviews from "@/pages/Reviews";
import ReviewWorkspace from "@/pages/ReviewWorkspace";
// Trainer Studio removed in Cycle 3.5 — org uses one default trainer via HeyGen integrations.
import IntegrationsRunway from "@/pages/IntegrationsRunway";
import IntegrationsHeyGen from "@/pages/IntegrationsHeyGen";
import AISettings from "@/pages/AISettings";
import PresentDemo from "@/pages/PresentDemo";
import LearnerDashboard from "@/pages/LearnerDashboard";
import CoursePlayer from "@/pages/CoursePlayer";
import QuizTaking from "@/pages/QuizTaking";
import MyCertificates from "@/pages/MyCertificates";
import { AdminAssignments, AdminLearners, AdminAssessments, AdminCertificates } from "@/pages/AdminLearning";
import FidelityAuditor from "@/pages/FidelityAuditor";
import AskAI from "@/pages/AskAI";
import { RevisionsList, RevisionCompare } from "@/pages/Revisions";
import OnboardingTour from "@/components/OnboardingTour";
import ManageContent from "@/pages/ManageContent";
import OnboardWizard from "@/pages/OnboardWizard";
import Usage from "@/pages/Usage";
import Home from "@/pages/Home";
import ProjectPage from "@/pages/ProjectPage";
import VideosList from "@/pages/VideosList";
import SettingsHub from "@/pages/SettingsHub";
import { useParams } from "react-router-dom";

function ToProject({ step }) {
    const { id } = useParams();
    return <Navigate to={`/videos/${id}?step=${step}`} replace />;
}

const PLACEHOLDER_ROUTES = [
    { path: "/analytics/learning", section: "Analytics", title: "Learning Analytics" },
    { path: "/analytics/content", section: "Analytics", title: "Content Analytics" },
    { path: "/admin/departments", section: "Admin", title: "Departments" },
];

function RoleRoot() {
    const { user } = useAuth();
    if (user?.role === "LEARNER") return <Navigate to="/learn" replace />;
    return <Home />;
}

export default function App() {
    return (
        <div className="app-root">
            <AuthProvider>
                <BrowserRouter>
                    <Routes>
                        <Route path="/login" element={<Login />} />
                        <Route path="/present/:id" element={<ProtectedRoute><PresentDemo /></ProtectedRoute>} />
                        <Route path="/learn" element={<ProtectedRoute><LearnerDashboard /><OnboardingTour /></ProtectedRoute>} />
                        <Route path="/learn/modules/:aid" element={<ProtectedRoute><CoursePlayer /><OnboardingTour /></ProtectedRoute>} />
                        <Route path="/learn/modules/:aid/quiz" element={<ProtectedRoute><QuizTaking /><OnboardingTour /></ProtectedRoute>} />
                        <Route path="/learn/certificates" element={<ProtectedRoute><MyCertificates /><OnboardingTour /></ProtectedRoute>} />
                        <Route
                            path="/"
                            element={
                                <ProtectedRoute>
                                    <AppShell />
                                </ProtectedRoute>
                            }
                        >
                            <Route index element={<RoleRoot />} />
                            <Route path="knowledge/documents" element={<Documents />} />
                            <Route path="knowledge/documents/:id" element={<DocumentDetail />} />
                            <Route path="knowledge/upload" element={<Upload />} />
                            <Route path="onboard" element={<OnboardWizard />} />
                            <Route path="knowledge/reviews" element={<Reviews />} />
                            <Route path="knowledge/reviews/:id" element={<ReviewWorkspace />} />
                            <Route path="knowledge/revisions" element={<RevisionsList />} />
                            <Route path="knowledge/revisions/:id" element={<RevisionCompare />} />
                            <Route path="ai/ask" element={<AskAI />} />
                            <Route path="content/trainer-studio" element={<Navigate to="/admin/integrations" replace />} />
                            <Route path="content/trainer-studio/:id" element={<Navigate to="/admin/integrations" replace />} />
                            <Route path="content/modules" element={<ManageContent />} />
                            <Route path="videos" element={<VideosList />} />
                            <Route path="videos/:id" element={<ProjectPage />} />
                            <Route path="settings" element={<SettingsHub />} />
                            <Route path="content/storyboards" element={<Navigate to="/videos" replace />} />
                            <Route path="content/storyboards/:id" element={<ToProject step="script" />} />
                            <Route path="content/storyboards/:id/fidelity" element={<FidelityAuditor />} />
                            <Route path="content/video-studio" element={<Navigate to="/videos" replace />} />
                            <Route path="content/video-studio/:id" element={<ToProject step="video" />} />
                            <Route path="learning/assignments" element={<AdminAssignments />} />
                            <Route path="learning/learners" element={<AdminLearners />} />
                            <Route path="learning/assessments" element={<AdminAssessments />} />
                            <Route path="learning/certificates" element={<AdminCertificates />} />
                            <Route path="admin/integrations" element={<IntegrationsRunway />} />
                            <Route path="admin/integrations/heygen" element={<IntegrationsHeyGen />} />
                            <Route path="admin/ai-settings" element={<AISettings />} />
                            <Route path="admin/users" element={<Users />} />
                            <Route path="admin/organisation" element={<Organisation />} />
                            <Route path="admin/branding" element={<Branding />} />
                            <Route path="admin/audit-log" element={<AuditLog />} />
                            <Route path="admin/usage" element={<Usage />} />
                            {PLACEHOLDER_ROUTES.map((p) => (
                                <Route
                                    key={p.path}
                                    path={p.path.replace(/^\//, "")}
                                    element={<Placeholder section={p.section} title={p.title} />}
                                />
                            ))}
                            <Route path="*" element={<Navigate to="/" replace />} />
                        </Route>
                    </Routes>
                </BrowserRouter>
                <Toaster position="top-right" toastOptions={{ duration: 6000 }} />
            </AuthProvider>
        </div>
    );
}
